// @ts-strict-ignore
import React, { type CSSProperties, useEffect, useState } from 'react';

import { addWeeks, format, isBefore } from 'date-fns';

import { send } from 'loot-core/platform/client/fetch';
import * as monthUtils from 'loot-core/shared/months';

import { useLocalPref } from '../hooks/useLocalPref';
import { useManualProgressTracking } from '../hooks/useProgressTracking';
import { AsyncStorageKey, useStorage } from '../hooks/useStorage';
import { SvgClose } from '../icons/v1';
import { theme } from '../style';

import { Link } from './common/Link';
import { Text } from './common/Text';
import { View } from './common/View';

type BackupWarningProps = {};

const viewStyle: CSSProperties = {
  minHeight: 50,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  gap: 5,
  padding: 10,
  paddingRight: 35,
};

function BackupWarning({}: BackupWarningProps) {
  const { markBackupExported } = useManualProgressTracking();
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [budgetId] = useLocalPref('id');
  const [cloudFileId] = useLocalPref('cloudFileId');
  const { getItem: getBackupWarningDate, setItem: setBackupWarningDate } =
    useStorage(
      AsyncStorageKey.BACKUP_WARNING_DATE.replace(
        '{budgetId}',
        budgetId,
      ) as any,
    );

  useEffect(() => {
    if (!cloudFileId) {
      (async () => {
        const storageData = await getBackupWarningDate();

        if (storageData) {
          const today = monthUtils.currentDate();

          if (isBefore(new Date(storageData), new Date(today.toISOString()))) {
            setShowWarning(true);
          }
        } else {
          await setNextWarningDate();
        }
      })();
    } else {
      setShowWarning(false);
    }
  }, [cloudFileId]);

  return showWarning ? (
    <View
      style={{
        position: 'relative',
        backgroundColor: theme.warningTextLight,
      }}
    >
      {error ? (
        <View style={{ ...viewStyle }}>
          An unknown error occurred while exporting.
        </View>
      ) : (
        <View style={{ ...viewStyle }}>
          <Text>
            It&#39;s important to backup your Good Steward data frequently.
            In case if you lose access to your data, you can import the backup
            anytime. Please export{' '}
            <Link
              variant="text"
              onClick={onExport}
              {...(isLoading ? { disabled: true } : {})}
              style={{ textDecorationLine: 'underline', color: 'white' }}
            >
              here
            </Link>
          </Text>
        </View>
      )}

      <View
        style={{
          position: 'absolute',
          top: 13,
          right: 20,
        }}
      >
        <Link variant="text" style={{ color: 'black' }} onClick={onClose}>
          <SvgClose width={10} />
        </Link>
      </View>
    </View>
  ) : null;

  async function onExport() {
    setIsLoading(true);
    setError(null);

    const response = await send('export-budget');

    if ('error' in response) {
      setError(response.error);
      setIsLoading(false);
      console.log('Export error code:', response.error);
      return;
    }

    window.Actual.saveFile(
      response.data,
      `${format(new Date(), 'yyyy-MM-dd')}-${budgetId}.zip`,
      'Export budget',
    );
    setIsLoading(false);
    // Mark export/backup task as completed
    markBackupExported();
    await onClose();
  }

  async function onClose() {
    await setNextWarningDate();
    setShowWarning(false);
  }

  async function setNextWarningDate() {
    const nextDate = addWeeks(monthUtils.currentDate(), 1);

    await setBackupWarningDate(nextDate.toISOString());
  }
}

export default BackupWarning;
